import { useEffect, useState, type ReactNode } from 'react'
import { api, type AccountDetail } from '../lib/api.ts'
import { ACCOUNT_TABS, type AccountTab } from '../lib/route.ts'
import { AccountConnection } from '../components/AccountConnection.tsx'
import { AccountDetails } from '../components/AccountDetails.tsx'
import { AccountErpCodes } from '../components/AccountErpCodes.tsx'
import { AccountErpReadWindow } from '../components/AccountErpReadWindow.tsx'
import { AccountRules } from '../components/AccountRules.tsx'
import { AccountStagePositions } from '../components/AccountStagePositions.tsx'
import { AccountTabs } from '../components/AccountTabs.tsx'
import { AccountTimeZone } from '../components/AccountTimeZone.tsx'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'

/**
 * One customer, a tab per part of it (#688).
 *
 * The page had grown into a column of sections, each saved on its own. Each
 * is a tab now, with an address, and still saves only what is its own. Every
 * tab stays mounted and only the one asked for is shown: what was typed in one
 * and not saved is still there after a look at another, and nothing is read
 * again on the way back.
 */
export function Account({ id, tab, onBack }: { id: string; tab: AccountTab; onBack: () => void }) {
  const [account, setAccount] = useState<AccountDetail | undefined>()
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    void api
      .account(id)
      .then((r) => setAccount(r.account))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [id])

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Contas
        </Button>
        <h1 className="text-tH4 text-text-strong">{account?.name ?? 'Conta'}</h1>
      </div>

      <AccountTabs accountId={id} active={tab} />

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {account === undefined
        ? error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
        : ACCOUNT_TABS.map((key) => (
            // Not the `hidden` attribute: a display utility on the same
            // element would win over it and show every tab at once.
            <div
              key={key}
              role="tabpanel"
              className={key === tab ? 'flex flex-col gap-5' : 'hidden'}
            >
              {panel(key, account, setAccount)}
            </div>
          ))}
    </div>
  )
}

/** What one tab holds. Every section but Dados and the rules reads its own data. */
function panel(
  tab: AccountTab,
  account: AccountDetail,
  onSaved: (next: AccountDetail) => void,
): ReactNode {
  switch (tab) {
    case 'details':
      return (
        <>
          <AccountDetails account={account} onSaved={onSaved} />
          {/* The pharmacy's clock (#674): its own save, because saving it
              moves the account's schedules. */}
          <AccountTimeZone accountId={account.id} />
        </>
      )
    case 'rules':
      return <AccountRules account={account} onSaved={onSaved} />
    case 'connection':
      // The ERP connection and where the prescriptions are, each saved apart (#689).
      return <AccountConnection accountId={account.id} />
    case 'stages':
      // Where its stages start on its own FC12540 (#673).
      return <AccountStagePositions accountId={account.id} />
    case 'read-window':
      // When its ERP may be read (#675), on the clock set under Dados.
      return <AccountErpReadWindow accountId={account.id} />
    case 'erp-codes':
      // What the installation's code columns hold against our tables (#672),
      // read through the connection once it answers.
      return <AccountErpCodes accountId={account.id} />
  }
}
